import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const SessionContext = createContext(null);
const KEY = "kiosk_active_shift";
const PORTAL_KEY = "kiosk_portal_unlocked";
const LV_KEY = "kiosk_liveview_unlocked";

export function SessionProvider({ children }) {
  const [shift, setShiftState] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [portalUnlocked, setPortalUnlocked] = useState(
    () => localStorage.getItem(PORTAL_KEY) === "1"
  );
  const [liveViewUnlocked, setLiveViewUnlocked] = useState(
    () => localStorage.getItem(LV_KEY) === "1"
  );
  const [pendingDraft, setPendingDraft] = useState(false);
  const [voucherPreview, setVoucherPreview] = useState(null);

  const setShift = (s) => {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
    setShiftState(s);
  };

  // Iter 29 · Pipeline 1 — Auto-rehydrate the active shift on mount.
  // Without this, a fresh browser session shows "Start a shift on Tab 1"
  // (greying out + ADD TO SHIFT) even when the backend already holds a
  // live shift for this tenant. We pull /shifts/active once; if there
  // is exactly one we bind it as the current session shift so the
  // calculator + invoices flow unlock immediately. Multi-shift tenants
  // keep the local-storage selection and we don't override it.
  useEffect(() => {
    if (shift) return; // already bound from localStorage
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shifts/active");
        if (cancelled) return;
        const list = Array.isArray(data) ? data.filter((s) => s.active !== false) : [];
        if (list.length === 1) {
          const s = list[0];
          localStorage.setItem(KEY, JSON.stringify(s));
          setShiftState(s);
        }
      } catch {
        /* offline or unauthorised — keep silent */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once on provider mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const unlockPortal = () => {
    localStorage.setItem(PORTAL_KEY, "1");
    setPortalUnlocked(true);
  };
  const lockPortal = () => {
    localStorage.removeItem(PORTAL_KEY);
    setPortalUnlocked(false);
  };
  const unlockLiveView = () => {
    localStorage.setItem(LV_KEY, "1");
    setLiveViewUnlocked(true);
  };
  const lockLiveView = () => {
    localStorage.removeItem(LV_KEY);
    setLiveViewUnlocked(false);
  };

  return (
    <SessionContext.Provider
      value={{
        shift, setShift,
        portalUnlocked, unlockPortal, lockPortal,
        liveViewUnlocked, unlockLiveView, lockLiveView,
        pendingDraft, setPendingDraft,
        voucherPreview, setVoucherPreview,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}
